import {expectOneOf, HttpClient, POST, ServerValidationResult, useHttpClient} from '@raison/network-facade';
import {type} from 'arktype';
import {SessionExpiredError, useLogout} from '~/entities/user';
import {AccountId} from '~/library/types';
import {BlockchainAccountType, OrderId} from '../model/BuyTxResource';

export type BuyTxRequestBody = {
    account: number;
    amount: number;
};

function prepareBuyTxRequest(id: AccountId, accountType: BlockchainAccountType, data: BuyTxRequestBody) {
    const [init, url] = (() => {
        switch (accountType) {
            case 'crypto':
                return [POST, `/v1/wallets/${id}/buy`];
            case 'stablecoin':
                return [POST, `/v1/tokens/${id}/buy`];
        }
    })();

    return [url, init(data)] as const;
}

const Order = type({
    id: OrderId,
});

function submitBuyTxRequest(
    http: HttpClient,
    id: AccountId,
    accountType: BlockchainAccountType,
    data: BuyTxRequestBody
) {
    const [url, init] = prepareBuyTxRequest(id, accountType, data);

    return http(url, init).then(
        expectOneOf({
            200: Order,
            201: Order,
            400: ServerValidationResult.to({amount: 'string'}),
            401: null,
        })
    );
}

export const useSubmitBuyTxRequest = (id: AccountId, accountType: BlockchainAccountType) => {
    const http = useHttpClient();
    const logout = useLogout();

    const mutateAsync = async (data: BuyTxRequestBody) => {
        const {status, json} = await submitBuyTxRequest(http, id, accountType, data);

        switch (status) {
            case 200:
            case 201:
                return {value: json.id};
            case 400:
                return {problem: json};
            case 401:
                logout();
                throw new SessionExpiredError();
        }
    };
    return {mutateAsync};
};
